import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AccialCreditNoteAssociationRequest,
  AccialCreditNoteAssociationResponse
} from 'src/app/core/interfaces/accial-credit-note';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { Utils } from 'src/app/core/resources/utils';
import { AccialService } from 'src/app/core/services/accial.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';

@Component({
  selector: 'app-cargo-credit-note-association',
  templateUrl: './cargo-credit-note-association.component.html',
  styleUrls: ['./cargo-credit-note-association.component.scss']
})
export class CargoCreditNoteAssociationComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  private _destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CargoCreditNoteAssociationComponent>,
    public utils: Utils,
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService,
    private accialService: AccialService,
  ) { }

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy() {
    this.spinner.hide();
    this._destroy$.next();
    this._destroy$.complete();
  }

  private initForm() {
    this.form = new FormGroup({
      associations: new FormArray([this.createAssociation()]),
    });
  }

  private createAssociation(): FormGroup {
    return new FormGroup({
      name: new FormControl('', Validators.required),
      number: new FormControl('', Validators.required),
    });
  }

  get associations(): FormArray {
    return this.form.get('associations') as FormArray;
  }

  /**
   * Mientras la asociación está en curso el diálogo no se puede cerrar, para
   * que la petición no se aborte y queden notas crédito a medio procesar.
   */
  private setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
    this.dialogRef.disableClose = isLoading;
  }

  close() {
    if (this.isLoading) return;
    this.dialogRef.close();
  }

  /**
   * Agrega una fila y le quita el foco al botón, para que no quede marcado
   * después del clic.
   */
  addAssociation($event?: Event) {
    this.associations.push(this.createAssociation());
    if ($event && $event.currentTarget instanceof HTMLElement) $event.currentTarget.blur();
  }

  removeAssociation(index: number) {
    if (this.associations.length <= 1) return;
    this.associations.removeAt(index);
  }

  /**
   * Quita los espacios sobrantes de cada campo para que un valor en blanco
   * no pase la validación de requerido.
   */
  private trimAssociations() {
    this.associations.controls.forEach((association: FormGroup) => {
      ['name', 'number'].forEach((field: string) => {
        const control = association.get(field);
        if (control && typeof control.value === 'string') control.setValue(control.value.trim());
      });
    });
  }

  onSubmit() {
    this.trimAssociations();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBarService.openSnackBar(
        'Completa la nota crédito y el número de factura en todas las filas', undefined, 'alert'
      );
      return;
    }
    const body: AccialCreditNoteAssociationRequest[] = this.associations.value;
    this.spinner.show();
    this.setLoading(true);
    this.accialService.associateCreditNotes(body)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (success: AccialCreditNoteAssociationResponse) => {
          this.spinner.hide();
          this.setLoading(false);
          this.dialogRef.close({ state: true, response: success });
        },
        (error) => {
          this.spinner.hide();
          this.setLoading(false);
          this.snackBarService.openSnackBar(
            error && error.error && error.error.message ? error.error.message : ServiceMessages.GENERAL_HTTP_ERROR,
            undefined, 'error', 8000
          );
        }
      );
  }
}
